import { Add, ExpandMore, HubOutlined } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import InternoModalConfirmacaoSaida from "../../../../../../components/modalSaindoDaPagina";
import api from "../../../../../../services/api";
import { carregarErpsSistema } from "../../../../../../services/erpSistema";
import ConexaoFormModal from "./conexaoFormModal";
import ErpCard from "./erpCard";
import ErpFormModal from "./erpFormModal";
import useStyles from "./styles";

const mensagemErro = (error, padrao) =>
  error?.response?.data?.error || error?.response?.data?.message || padrao;

export default function Erp() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [dados, setDados] = useState({
    tiposEmUso: [],
    catalogo: {},
    erps: [],
  });
  // Busca só depois da primeira abertura do accordion.
  const [jaAberto, setJaAberto] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  // { erp } -> edição; { } -> criação
  const [erpModal, setErpModal] = useState(null);
  // { erp, categoria, conexao? }
  const [conexaoModal, setConexaoModal] = useState(null);
  // { tipo: "erp" | "conexao", erp, conexao? }
  const [exclusao, setExclusao] = useState(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const response = await api.get("/interno/configErp/findMany");
      setDados(response.data);
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: mensagemErro(error, "Erro ao buscar as configurações de ERP"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jaAberto) carregar();
  }, [jaAberto]);

  const executar = async (requisicao, sucesso, falha, aoConcluir) => {
    setSalvando(true);
    try {
      await requisicao();
      aoConcluir();
      setSnackbar({ severity: "success", children: sucesso });
      // ERP criado/removido: atualiza o que o front sabe sem esperar o F5.
      carregarErpsSistema();
      await carregar();
    } catch (error) {
      console.error(error);
      setSnackbar({ severity: "error", children: mensagemErro(error, falha) });
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvarErp = (form) =>
    executar(
      () => api.post("/interno/configErp/upsertErp", form),
      form.id ? "ERP atualizado com sucesso" : "ERP adicionado com sucesso",
      "Não foi possível salvar o ERP",
      () => setErpModal(null),
    );

  const handleSalvarConexao = (form) =>
    executar(
      () => api.post("/interno/configErp/upsertConfig", form),
      "Dados de acesso salvos com sucesso",
      "Não foi possível salvar os dados de acesso",
      () => setConexaoModal(null),
    );

  const handleConfirmarExclusao = () =>
    executar(
      () =>
        exclusao.tipo === "erp"
          ? api.post("/interno/configErp/deleteErp", { id: exclusao.erp.id })
          : api.post("/interno/configErp/deleteConfig", {
              id: exclusao.conexao.id,
            }),
      exclusao.tipo === "erp" ? "ERP removido" : "Conexão removida",
      "Não foi possível remover",
      () => setExclusao(null),
    ).then(() => setExclusao(null)); // na recusa também: a mensagem fica no snackbar

  const tituloCategoria = (erp, categoria) =>
    dados.catalogo?.[erp?.tipo]?.categorias?.[categoria]?.titulo || categoria;

  return (
    <>
      <Accordion
        data-secao="erp"
        onChange={(_, aberto) => aberto && setJaAberto(true)}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={styles.headerTitleRow}>
            <HubOutlined color="primary" />
            <Typography variant="h5" sx={styles.title}>
              Integração com ERP
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={styles.details}>
          <Box sx={styles.toolbar}>
            <Typography sx={styles.toolbarCount}>
              {loading
                ? ""
                : `${dados.erps.length} ERP${dados.erps.length === 1 ? "" : "s"} cadastrado${dados.erps.length === 1 ? "" : "s"}`}
            </Typography>
            <Button
              data-busca="erp-adicionar"
              variant="contained"
              startIcon={<Add />}
              onClick={() => setErpModal({})}
              disabled={loading}
            >
              Adicionar ERP
            </Button>
          </Box>

          {loading && !dados.erps.length ? (
            <Box sx={styles.loadingState}>
              <CircularProgress />
            </Box>
          ) : dados.erps.length === 0 ? (
            <Box sx={styles.emptyState}>
              <HubOutlined sx={styles.emptyIcon} />
              <Typography sx={styles.emptyTitle}>
                Nenhum ERP cadastrado
              </Typography>
              <Typography sx={styles.emptyDescription}>
                Clique em "Adicionar ERP" para cadastrar o primeiro e configurar
                seus dados de acesso.
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.lista} data-busca="erp-lista">
              {dados.erps.map((erp, index) => (
                <ErpCard
                  key={erp.id}
                  erp={erp}
                  catalogo={dados.catalogo?.[erp.tipo]}
                  // O backend decide: ERP cadastrado tem prioridade sobre a
                  // variável de ambiente ERP.
                  ativoNoSistema={erp.emUso}
                  defaultExpanded={index === 0 || erp.emUso}
                  onEditar={() => setErpModal({ erp })}
                  onExcluir={() => setExclusao({ tipo: "erp", erp })}
                  onAdicionarConexao={(categoria) =>
                    setConexaoModal({ erp, categoria })
                  }
                  onEditarConexao={(conexao) =>
                    setConexaoModal({
                      erp,
                      categoria: conexao.categoria,
                      conexao,
                    })
                  }
                  onExcluirConexao={(conexao) =>
                    setExclusao({ tipo: "conexao", erp, conexao })
                  }
                />
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <ErpFormModal
        open={!!erpModal}
        erp={erpModal?.erp}
        tiposDisponiveis={Object.keys(dados.catalogo || {})}
        loading={salvando}
        onClose={() => setErpModal(null)}
        onSave={handleSalvarErp}
      />

      <ConexaoFormModal
        open={!!conexaoModal}
        erp={conexaoModal?.erp}
        categoria={conexaoModal?.categoria}
        definicao={
          dados.catalogo?.[conexaoModal?.erp?.tipo]?.categorias?.[
            conexaoModal?.categoria
          ]
        }
        conexao={conexaoModal?.conexao}
        loading={salvando}
        onClose={() => setConexaoModal(null)}
        onSave={handleSalvarConexao}
      />

      <InternoModalConfirmacaoSaida
        openConfirmacao={!!exclusao}
        setOpenConfirmacao={(open) => !open && setExclusao(null)}
        confirmLoad={salvando}
        execute={handleConfirmarExclusao}
        cancel={() => setExclusao(null)}
        severity="error"
        title={exclusao?.tipo === "erp" ? "Excluir ERP" : "Excluir conexão"}
        headerText={
          exclusao?.tipo === "erp"
            ? `Excluir o ERP "${exclusao?.erp?.nome}"?`
            : `Excluir a conexão de ${tituloCategoria(exclusao?.erp, exclusao?.conexao?.categoria)} #${exclusao?.conexao?.posicao} de "${exclusao?.erp?.nome}"?`
        }
        contentText={
          exclusao?.tipo === "erp"
            ? "Todos os dados de acesso de banco e API deste ERP também serão removidos. Se ele estiver em uso, o sistema voltará a usar as variáveis de ambiente."
            : exclusao?.conexao?.emUso
              ? "Esta conexão está em uso. Ao removê-la, o sistema passará a usar a próxima conexão cadastrada ou, se não houver, as variáveis de ambiente."
              : "Os dados de acesso desta conexão serão removidos permanentemente."
        }
        confirmText="Excluir"
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={snackbar?.severity === "success" ? 4000 : 6000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
